import { DateTimeFormatter, LocalDate, LocalTime } from "@js-joda/core";
import React from "react";
import { writesToRetry, writesToSend, type AnswerWrite } from "./answerWrites";

// Pure helpers (plus one tiny hook) that keep inline editors from overwriting stored answers by
// accident: Save is enabled only after the reviewer changed something AND the resulting answer
// differs from the stored one, so opening an editor and saving can never coerce or erase a value
// (e.g. a stored date the editor cannot parse, or a radio value that is no longer an option).

export const TIME_FORMAT = DateTimeFormatter.ofPattern("HH:mm");

/** Stored value parsed for an editor; `unreadable` keeps the original text when parsing failed. */
export interface ParsedStoredValue<T> {
  parsed: T | null;
  unreadable: string | null;
}

function parseStored<T>(value: unknown, parse: (text: string) => T): ParsedStoredValue<T> {
  if (typeof value !== "string" || value.trim() === "") {
    return { parsed: null, unreadable: null };
  }
  try {
    return { parsed: parse(value.trim()), unreadable: null };
  } catch {
    return { parsed: null, unreadable: value };
  }
}

/** Dates are stored "yyyy-MM-dd" (mobile `<input type="date">`). */
export function parseStoredDate(value: unknown): ParsedStoredValue<LocalDate> {
  return parseStored(value, (text) => LocalDate.parse(text));
}

/** Times are stored "HH:mm" (mobile `<input type="time">`). */
export function parseStoredTime(value: unknown): ParsedStoredValue<LocalTime> {
  return parseStored(value, (text) => LocalTime.parse(text));
}

function normalize(value: unknown): unknown {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  if (Array.isArray(value)) {
    const items = (value as unknown[]).map(normalize);
    return items.every((item) => item === null) ? null : items;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(record)
        .sort()
        .map((key) => [key, normalize(record[key])])
    );
  }
  return value;
}

const NUMBER_TEXT = /^-?(\d+(\.\d+)?|\.\d+)$/;

/**
 * Whether a number / blood-pressure input's text is a number to save ("5", "-2", "5.5",
 * ".5"), not partial input such as "5.", "-", "--" or "+". The editor form is `noValidate` (see
 * EditorShell), so Chakra NumberInputField's native pattern does not stop Enter from saving it.
 */
export function isNumberText(text: string): boolean {
  return NUMBER_TEXT.test(text.trim());
}

/** True when saving `next` would actually change the stored answer (null / "" / [] are equal). */
export function isAnswerChanged(stored: unknown, next: unknown): boolean {
  return JSON.stringify(normalize(stored)) !== JSON.stringify(normalize(next));
}

/** Editor draft state that remembers whether the reviewer touched it. */
export function useEditorDraft<T>(initial: () => T) {
  const [draft, setDraft] = React.useState(initial);
  const [isTouched, setIsTouched] = React.useState(false);

  const updateDraft = React.useCallback((next: React.SetStateAction<T>) => {
    setIsTouched(true);
    setDraft(next);
  }, []);

  return { draft, isTouched, updateDraft };
}

/**
 * Runs an editor's multi-answer save (`onSaveAnswers`). After a failure the next `run` (Retry)
 * sends only the writes that were left (`pending`), whatever it is given; `reset` drops them once
 * the draft changes, so the next save is planned from the draft again. While writes are pending
 * the editor keeps Save / Retry enabled (`canSubmitWrites`).
 */
export function useAnswerWriter(onSaveAnswers: (writes: AnswerWrite[]) => Promise<void>) {
  const [remaining, setRemaining] = React.useState<AnswerWrite[] | null>(null);

  const run = async (writes: AnswerWrite[]): Promise<boolean> => {
    const toSend = writesToSend(remaining, writes);
    try {
      await onSaveAnswers(toSend);
      setRemaining(null);
      return true;
    } catch (error) {
      // Shown through the editor's `error`
      setRemaining(writesToRetry(error, toSend));
      return false;
    }
  };

  const reset = React.useCallback(() => setRemaining(null), []);

  return { pending: remaining, reset, run };
}
